import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import programService from '@/shared/services/programService';

export const DEFAULT_RESPONSE_DAYS = 14;
const DAYS_MIN = 1;
const DAYS_MAX = 60;

const problemWith = (text) => {
    if (text.trim() === '') return '';
    const n = Number(text);
    if (!Number.isInteger(n) || n < DAYS_MIN || n > DAYS_MAX) {
        return `Enter a whole number of days from ${DAYS_MIN} to ${DAYS_MAX}.`;
    }
    return '';
};

/**
 * How many days a student has to answer an offer when the person making it
 * does not choose a date. Empty means the usual 14. The offerer can still
 * pick a date for one offer.
 */
function OfferResponseDaysCard({ programType }) {
    const [days, setDays] = useState('');
    const [saved, setSaved] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [notice, setNotice] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const config = await programService
                .getConfig(programType)
                .catch((err) => {
                    // A program with no saved configuration yet has no setting
                    if (err.response?.status === 404) return {};
                    throw err;
                });
            const value = config.offerResponseDays ?? null;
            setSaved(value);
            setDays(value === null ? '' : String(value));
        } catch (_err) {
            setLoadError('Failed to load the days to answer an offer.');
        } finally {
            setLoading(false);
        }
    }, [programType]);

    useEffect(() => {
        load();
    }, [load]);

    const problem = problemWith(days);
    const unchanged =
        (days.trim() === '' && saved === null) || Number(days) === saved;
    const inputId = `offer-response-days-${programType}`;
    const problemId = `${inputId}-problem`;

    const save = async (event) => {
        event.preventDefault();
        if (problem || saving) return;
        setSaving(true);
        setSaveError(null);
        setNotice('');
        const value = days.trim() === '' ? null : Number(days);
        try {
            const config = await programService.upsertConfig(programType, {
                offerResponseDays: value,
            });
            const stored = config?.offerResponseDays ?? value;
            setSaved(stored);
            setDays(stored === null ? '' : String(stored));
            setNotice(
                stored === null
                    ? `Offers now give ${DEFAULT_RESPONSE_DAYS} days to answer.`
                    : `Offers now give ${stored} ${stored === 1 ? 'day' : 'days'} to answer.`
            );
        } catch (err) {
            setSaveError(
                err.response?.data?.message ||
                    'Failed to save the days to answer an offer.'
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-4" role="status">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading the days to answer an offer...
            </div>
        );
    }
    if (loadError) return <Alert variant="danger">{loadError}</Alert>;

    return (
        <Form onSubmit={save} noValidate>
            <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                    <h2 className="h5">Days to answer an offer</h2>
                    <p className="text-body-secondary">
                        A student has this many days to accept or decline an
                        offer. The offer ends at 11:59 PM Mountain time on the
                        last day, and the student is reminded two days before.
                        Whoever makes an offer can choose a different date for
                        it.
                    </p>
                    <Form.Group
                        controlId={inputId}
                        className="mb-3 col-12 col-sm-8 col-md-5 col-lg-4"
                    >
                        <Form.Label>Days to answer</Form.Label>
                        <Form.Control
                            type="number"
                            inputMode="numeric"
                            min={DAYS_MIN}
                            max={DAYS_MAX}
                            step={1}
                            value={days}
                            placeholder={`Program default (${DEFAULT_RESPONSE_DAYS} days)`}
                            isInvalid={problem !== ''}
                            aria-describedby={problem ? problemId : undefined}
                            onChange={(event) => setDays(event.target.value)}
                        />
                        <Form.Control.Feedback type="invalid" id={problemId}>
                            {problem}
                        </Form.Control.Feedback>
                    </Form.Group>
                    {saveError && (
                        <Alert variant="danger" role="alert">
                            {saveError}
                        </Alert>
                    )}
                    <div role="status">{notice}</div>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={saving || problem !== '' || unchanged}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </Card.Body>
            </Card>
        </Form>
    );
}

export default OfferResponseDaysCard;
